// components/InventoryList.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import ItemsNeededByDate from './ItemsNeededByDate';
import { useAuth } from '../contexts/AuthContext';
import { Table } from './common/Table';
import { Loading } from './common/Loading';

const apiUrl = window._env_?.REACT_APP_API_URL || 'https://app.mylocaleventrentals.com/api';

const InventoryList = () => {
  const [inventory, setInventory] = useState([]);
  const [totalCost, setTotalCost] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { logout, getToken } = useAuth();

  const today = new Date();

  const columns = [
    { key: 'name', label: 'Name', sortable: true },
    {
      key: 'isActive',
      label: 'Active',
      sortable: true,
      render: (v) => (
        <Chip
          size="small"
          label={v ? 'Active' : 'Inactive'}
          color={v ? 'success' : 'default'}
          variant={v ? 'filled' : 'outlined'}
        />
      ),
    },
    {
      key: 'summary',
      label: 'Summary',
      sortable: false,
      render: (_, row) => {
        const t = (row.shortDescription || row.description || '').trim();
        if (!t) return '—';
        return t.length > 55 ? `${t.slice(0, 55)}…` : t;
      },
    },
    {
      key: 'imageCount',
      label: 'Imgs',
      sortable: false,
      render: (_, row) => {
        const imgs = row.images;
        if (!Array.isArray(imgs)) return 0;
        return imgs.filter((x) => (typeof x === 'string' ? x.trim() : x && x.url)).length;
      },
    },
    { key: 'description', label: 'Description', sortable: false },
    {
      key: 'quantity',
      label: 'Quantity',
      sortable: false,
      editable: true,
      editType: 'number',
    },
    {
      key: 'purchasePrice',
      label: 'Purchase unit price',
      sortable: false,
      render: (value) => `$${parseFloat(value || 0).toFixed(2)}`,
      editable: true,
      editType: 'number',
    },
    {
      key: 'price',
      label: 'Rental price',
      sortable: false,
      editable: true,
      editType: 'number',
      render: (value) => `$${parseFloat(value || 0).toFixed(2)}`,
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (value, row, { isEditing, handleEdit, handleSave, handleCancel }) => {
        if (isEditing) {
          return (
            <Stack direction="row" spacing={1} flexWrap="wrap" onClick={(e) => e.stopPropagation()}>
              <Button
                size="small"
                variant="contained"
                color="success"
                onClick={(e) => {
                  e.stopPropagation();
                  handleSave();
                }}
              >
                Save
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="inherit"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCancel();
                }}
              >
                Cancel
              </Button>
            </Stack>
          );
        }

        return (
          <Stack direction="row" spacing={1} flexWrap="wrap" onClick={(e) => e.stopPropagation()}>
            <Button
              size="small"
              variant="outlined"
              onClick={(e) => {
                e.stopPropagation();
                handleEdit(row);
              }}
            >
              Edit
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="error"
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteItem(row);
              }}
            >
              Delete
            </Button>
          </Stack>
        );
      },
    },
  ];

  useEffect(() => {
    const fetchData = async () => {
      const token = getToken();
      if (!token) {
        logout();
        navigate('/login');
        return;
      }

      setError('');
      setLoading(true);

      try {
        const inventoryResponse = await fetch(`${apiUrl}/inventory`, {
          headers: {
            'Content-Type': 'application/json',
            'x-auth-token': token,
          },
        });

        if (inventoryResponse.status === 401) {
          logout();
          navigate('/login');
          return;
        }

        const inventoryData = await inventoryResponse.json();
        if (inventoryResponse.ok) {
          const items = inventoryData.items || inventoryData;
          setInventory(items);
          setTotalCost(
            inventoryData.totalCost ??
              (Array.isArray(items)
                ? items.reduce(
                    (sum, it) => sum + parseFloat(it.purchasePrice || 0) * parseInt(it.quantity || 0, 10),
                    0
                  )
                : 0)
          );
        } else {
          setError(inventoryData.message || 'Error loading inventory data');
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [getToken, logout, navigate]);

  const handleNew = () => {
    navigate('/inventory/new');
  };

  const handleClick = (uuid) => {
    navigate(`/inventory/${uuid}/edit`);
  };

  const handleSave = async (editedRow) => {
    const token = getToken();
    if (!token) return;

    try {
      const response = await fetch(`${apiUrl}/inventory/${editedRow.uuid}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token,
        },
        body: JSON.stringify(editedRow),
      });

      if (response.ok) {
        setInventory((prev) => {
          const next = prev.map((it) => (it.uuid === editedRow.uuid ? editedRow : it));
          setTotalCost(
            next.reduce(
              (sum, item) => sum + parseFloat(item.purchasePrice || 0) * parseInt(item.quantity || 0, 10),
              0
            )
          );
          return next;
        });
      } else {
        const errorData = await response.json();
        window.alert(errorData.message || 'Failed to update item');
      }
    } catch (e) {
      console.error('Error saving item:', e);
      window.alert('Failed to save changes. Please try again.');
    }
  };

  const handleDeleteItem = async (item) => {
    if (!window.confirm(`Are you sure you want to delete "${item.name}"?`)) {
      return;
    }

    const token = getToken();
    if (!token) return;

    try {
      const response = await fetch(`${apiUrl}/inventory/${item.uuid}`, {
        method: 'DELETE',
        headers: {
          'x-auth-token': token,
        },
      });

      if (response.ok) {
        setInventory((prev) => {
          const next = prev.filter((i) => i.uuid !== item.uuid);
          setTotalCost(
            next.reduce(
              (sum, i) => sum + parseFloat(i.purchasePrice || 0) * parseInt(i.quantity || 0, 10),
              0
            )
          );
          return next;
        });
      } else {
        const errorData = await response.json();
        window.alert(errorData.message || 'Failed to delete item');
      }
    } catch (e) {
      console.error('Error deleting item:', e);
      window.alert('Failed to delete item. Please try again.');
    }
  };

  if (loading) {
    return <Loading text="Loading inventory…" fullScreen />;
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3, px: { xs: 2, sm: 3 } }}>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h4" component="h1" fontWeight={700} color="text.primary" gutterBottom>
            Inventory
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage items, stock levels, and rental pricing
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleNew} sx={{ alignSelf: 'flex-start' }}>
            Add new item
          </Button>
        </Stack>

        <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
          {inventory.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Typography color="text.secondary" gutterBottom>
                No inventory items yet.
              </Typography>
              <Button variant="outlined" startIcon={<AddIcon />} onClick={handleNew} sx={{ mt: 1 }}>
                Add your first item
              </Button>
            </Box>
          ) : (
            <Table
              columns={columns}
              data={inventory}
              striped
              hoverable
              material
              searchable
              searchPlaceholder="Search by name, description..."
              onRowClick={(row) => handleClick(row.uuid)}
              paginated
              pageSize={10}
              rowKey="uuid"
              onSave={handleSave}
            />
          )}
        </Paper>

        {inventory.length > 0 && (
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: 'grey.50' }}>
            <Typography variant="subtitle1" align="right" fontWeight={600}>
              Total cost:{' '}
              <Box component="span" sx={{ color: 'success.main' }}>
                ${totalCost.toFixed(2)}
              </Box>
            </Typography>
          </Paper>
        )}

        <Box>
          <ItemsNeededByDate defaultDate={today} />
        </Box>
      </Stack>
    </Container>
  );
};

export default InventoryList;
